import { FC, useState } from 'react';
import { Card } from 'shared/ui/card/card';
import { useTranslation } from 'hooks';
import { Table } from 'shared/ui/table/table';
import { TablePagination } from 'shared/ui/table/table-pagination';
import { TableColumn } from 'shared/ui/table/table.types';
import {
  useCreateBillingPortalSessionMutation,
  useGetBillingSpendQuery,
} from 'store/api/apiSlice';
import { formatMonthlyPrice } from 'shared/helpers/formatMonthlyPrice';
import { CURRENT_SPEND_PAGE_SIZE } from 'shared/constants/billing';
import Button from 'shared/ui/button/button';
import { snackbar } from 'shared/helpers/snackbar';
import cls from './current-spend.module.scss';
import { SpendRow } from './current-spend.types';

export const CurrentSpend: FC = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetBillingSpendQuery();
  const [createBillingPortalSession, { isLoading: isCreatingPortalSession }] =
    useCreateBillingPortalSessionMutation();
  const [page, setPage] = useState(1);

  const handleOpenBillingPortal = async () => {
    try {
      const { url } = await createBillingPortalSession().unwrap();
      window.location.href = url;
    } catch {
      snackbar(t('manageBillingDetailsError'));
    }
  };

  const websites = data?.websites ?? [];
  const currency = data?.currency ?? 'USD';
  const totalAmount = data?.total ?? 0;

  const formatMonthly = (value: number) =>
    formatMonthlyPrice(value, currency, t('monthShort'));

  const hasRows = websites.length > 0;

  const pageCount = Math.max(
    1,
    Math.ceil(websites.length / CURRENT_SPEND_PAGE_SIZE)
  );
  const currentPage = Math.min(page, pageCount);

  const pagedWebsites = websites.slice(
    (currentPage - 1) * CURRENT_SPEND_PAGE_SIZE,
    currentPage * CURRENT_SPEND_PAGE_SIZE
  );

  const rows: SpendRow[] = pagedWebsites.map(r => ({
    id: String(r.license_id),
    website: r.domain_name ?? t('notAssigned'),
    subscription: r.subscription,
    monthly: r.monthly_price,
  }));

  const columns: TableColumn<SpendRow>[] = [
    {
      id: 'website',
      header: t('website'),
      renderCell: row => (
        <div className={cls.websiteCell}>
          <span className={cls.websiteName}>{row.website}</span>
          <span className={cls.websiteSub}>{row.subscription}</span>
        </div>
      ),
      renderFooter: hasRows ? () => t('totalAmount') : undefined,
    },
    {
      id: 'monthly',
      header: t('monthly'),
      align: 'right',
      renderCell: row => formatMonthly(row.monthly),
      renderFooter: hasRows ? () => formatMonthly(totalAmount) : undefined,
    },
  ];

  return (
    <Card className={cls.currentSpend}>
      <div className={cls.heading}>
        <div className={cls.headingRow}>
          <h2>{t('currentSpend')}</h2>
          <Button
            theme='tonal'
            tone='default'
            onClick={handleOpenBillingPortal}
            loading={isCreatingPortalSession}
            disabled={isCreatingPortalSession}
          >
            {t('manageBillingDetails')}
          </Button>
        </div>
        {hasRows && (
          <div className={cls.total}>
            <span>
              {websites.length} {t('subscriptionsCount')}
            </span>
            <span> · </span>
            <span>{formatMonthlyPrice(totalAmount, currency, t('month'))}</span>
          </div>
        )}
      </div>

      <Table
        columns={columns}
        rows={rows}
        getRowKey={row => row.id}
        isLoading={isLoading}
        emptyState={<p className={cls.noResults}>{t('noSubscriptions')}</p>}
      ></Table>
      <TablePagination
        page={currentPage}
        pageSize={CURRENT_SPEND_PAGE_SIZE}
        total={websites.length}
        onPageChange={setPage}
      />
    </Card>
  );
};
